import { StartGameContent } from "../StartGameContent";
import { PostGamePlayView } from "./PostGamePlayView"; 
import { useState, useEffect } from "react";
import { AlreadyPlayedModal } from "./AlreadyPlayedModal";
import { Dialog } from "@/components/ui/dialog";
import { hasCompletedTodaysPuzzle, getTodayString } from "@/lib/utils/date-utils";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/game/LoadingSpinner";

interface StartContainerProps {
  userEmail: string | null;
  onGameStart: (email?: string) => void;
  onViewPreviousResults: () => void;
  hasCompletedToday: boolean;
  isLoading?: boolean;
  onClearCompletedStatus?: () => void;
}

export function StartContainer({ 
  userEmail, 
  onGameStart,
  onViewPreviousResults,
  hasCompletedToday,
  isLoading = false,
  onClearCompletedStatus
}: StartContainerProps) {
  const [showAlreadyPlayedModal, setShowAlreadyPlayedModal] = useState(false);
  
  // Set the default spreadsheet URL if none exists
  useEffect(() => {
    const defaultUrl = "https://docs.google.com/spreadsheets/d/1eLxTlh8OqQhHK_YY6lDyI5FQ5NcntiHC5miJsu2WQjQ/edit?usp=sharing";
    
    if (!localStorage.getItem("external_puzzle_url")) {
      localStorage.setItem("external_puzzle_url", defaultUrl);
    }
  }, []);

  // Check if we should auto-view results on component mount
  useEffect(() => {
    const viewingResults = localStorage.getItem("viewing_results") === "true";
    const completedDate = localStorage.getItem("completed_date");
    const todayString = getTodayString();
    
    console.log("StartContainer: Check for auto-viewing results:", {
      viewingResults,
      completedDate,
      todayString,
      completedToday: completedDate === todayString
    });
    
    if (viewingResults && (completedDate === todayString) && hasCompletedToday) {
      console.log("StartContainer: Auto-viewing results based on localStorage flag");
      onViewPreviousResults();
    }
  }, [hasCompletedToday, onViewPreviousResults]);

  // Double-check hasCompletedToday directly
  useEffect(() => {
    const actuallyCompletedToday = hasCompletedTodaysPuzzle();
    const todayString = getTodayString();
    const completedDate = localStorage.getItem("completed_date");
    const lastGameResult = localStorage.getItem("last_game_result");
    
    console.log("StartContainer: Direct check for game completion:", {
      propsHasCompletedToday: hasCompletedToday,
      actuallyCompletedToday,
      todayString,
      completedDate,
      hasLastGameResult: !!lastGameResult,
    });
  }, [hasCompletedToday, onViewPreviousResults]);

  const handleStartClick = () => {
    console.log("StartContainer: Start game button clicked, hasCompletedToday:", hasCompletedToday);
    
    // If user has completed today's puzzle, show modal
    if (hasCompletedToday) {
      setShowAlreadyPlayedModal(true);
    } else {
      // Otherwise start the game
      console.log("StartContainer: Initiating game start sequence with email:", userEmail);
      if (userEmail) {
        console.log("StartContainer: Calling onGameStart with email:", userEmail);
        onGameStart(userEmail);
      } else {
        console.error("Cannot start game: no email available");
      }
    }
  };

  const handleViewResults = () => {
    console.log("View results button clicked directly");
    localStorage.setItem("viewing_results", "true");
    onViewPreviousResults();
  };

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <div className="container mx-auto py-8 px-4 relative">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        ) : hasCompletedToday ? (
          <PostGamePlayView onViewResults={handleViewResults} />
        ) : (
          <StartGameContent 
            onStart={handleStartClick} 
            hasCompletedToday={hasCompletedToday}
          />
        )}
        
        <AlreadyPlayedModal
          open={showAlreadyPlayedModal}
          onOpenChange={setShowAlreadyPlayedModal}
          onViewResults={handleViewResults}
        />
      </div>
    </div>
  );
}
